import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import type { FieldExplainerContent } from '@/content/field-explainers/types';

type FieldExplainerGuideProps = {
  title: string;
  description: string;
  entries: FieldExplainerContent[];
};

/**
 * Static, always-rendered version of the per-field explainers. The popovers
 * answer "what is this?" in the moment; this section makes the same content
 * readable end-to-end (and crawlable) with the reader's live numbers filled in.
 */
export function FieldExplainerGuide({ title, description, entries }: FieldExplainerGuideProps) {
  return (
    <Card className="brand-frame">
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 md:grid-cols-2">
          {entries.map((entry) => (
            <article key={entry.id} className="space-y-2.5 rounded-xl border border-border/70 bg-background/50 p-4">
              <div className="flex items-baseline justify-between gap-3">
                <h3 className="font-display text-base text-foreground">{entry.label}</h3>
                <Badge variant="outline" className="shrink-0 border-border px-1.5 py-0.5 text-[9px] uppercase tracking-[0.12em] text-muted-foreground">
                  {entry.kind}
                </Badge>
              </div>
              <p className="text-[13px] leading-relaxed text-foreground">{entry.plain}</p>
              <pre className="overflow-x-auto whitespace-pre rounded-md border border-border/60 bg-card px-3 py-2 font-mono text-[11px] leading-relaxed text-foreground">
                {entry.formula}
              </pre>
              <p className="text-xs leading-relaxed text-muted-foreground">{entry.feeds}</p>
              {entry.assumption ? (
                <div className="flex gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2">
                  <span className="shrink-0 text-[10px] font-bold uppercase tracking-[0.1em] text-amber-700 dark:text-amber-300">Assumption</span>
                  <span className="text-xs leading-relaxed text-foreground">{entry.assumption}</span>
                </div>
              ) : null}
            </article>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
